/* chaseLadder.js — frontend mirror of `chase_ladder_v2.compute_rung`.
 *
 * Used by surfaces that already have the activity feed in hand (e.g.
 * Documents Hub) so they can compute the rung locally without an
 * extra round-trip. The Today sheet receives the rung pre-computed
 * from `/api/today/sheet` — surfaces should prefer the API value
 * when present and only fall back to this mirror when needed.
 *
 * Outputs the same shape as the backend so consumers don't branch.
 */
const _MS_DAY = 86_400_000;
const _MS_24H = 86_400_000;

const _CHASE_TITLE_PREFIX = "Chase sent · ";

function _parseAt(v) {
  if (!v) return null;
  const t = typeof v === "number" ? v : Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

function _kind(a) {
  return String(a?.kind || a?.activity_type || a?.type || "").toLowerCase();
}
function _title(a) {
  return String(a?.title || a?.text || a?.body || "");
}
function _refItem(a) {
  if (!a) return null;
  return a.ref_item_id || a.meta?.item_id || a.meta?.ref_item_id || null;
}
function _at(a) {
  return _parseAt(a?.at || a?.created_at);
}

function _targets(act, id, title) {
  const ref = _refItem(act);
  if (ref && (ref === id || ref === title)) return true;
  const t = _title(act);
  if (t.startsWith(_CHASE_TITLE_PREFIX)) {
    return t.slice(_CHASE_TITLE_PREFIX.length) === title;
  }
  if (t.startsWith("Risk escalated: ") && (title && t.includes(title))) return true;
  return false;
}

function _isChase(a) {
  const k = _kind(a);
  if (k === "chase" || k === "chase_logged") return true;
  if (k === "reminder_sent" && _title(a).startsWith(_CHASE_TITLE_PREFIX)) return true;
  return false;
}

function _isEscalation(a) {
  const k = _kind(a);
  if (k === "risk" || k === "risk_flagged" || k === "escalation_flagged") return true;
  if (_title(a).startsWith("Risk escalated:")) return true;
  return false;
}

const RUNG_LABEL = {
  awaiting: "Awaiting",
  chased_recently: "Chased recently",
  awaiting_response: "Awaiting response",
  escalation_suggested: "Escalation suggested",
  manager_flagged: "Manager visibility",
  high_chain_risk: "High chain risk",
};

export function computeRung({
  blockerId,
  blockerTitle,
  severity = "info",
  activities = [],
  daysToExchange = null,
} = {}) {
  if (!blockerId && !blockerTitle) {
    return {
      rung: "awaiting",
      label: RUNG_LABEL.awaiting,
      chase_count: 0,
      last_chase_at: null,
      days_since_chase: null,
      manager_flagged: false,
      manager_flagged_at: null,
      next_action: "send_chase",
    };
  }
  const acts = (activities || []).filter((a) => _targets(a, blockerId, blockerTitle));
  const chases = acts.filter(_isChase);
  let lastChaseAt = null;
  for (const a of chases) {
    const ts = _at(a);
    if (ts && (lastChaseAt === null || ts > lastChaseAt)) lastChaseAt = ts;
  }
  const chaseCount = chases.length;
  const daysSinceChase = lastChaseAt ? Math.floor((Date.now() - lastChaseAt) / _MS_DAY) : null;

  const escalations = acts.filter(_isEscalation);
  let managerFlaggedAt = null;
  for (const a of escalations) {
    const ts = _at(a);
    if (ts && (managerFlaggedAt === null || ts > managerFlaggedAt)) managerFlaggedAt = ts;
  }
  const managerFlagged = managerFlaggedAt !== null;

  const inExchangeWindow = daysToExchange !== null && daysToExchange !== undefined && daysToExchange >= -1 && daysToExchange <= 7;
  const isBlocking = severity === "blocking";

  let rung, nextAction;
  if (inExchangeWindow && isBlocking && (chaseCount >= 1 || managerFlagged)) {
    rung = "high_chain_risk";
    nextAction = managerFlagged ? "review" : "escalate";
  } else if (managerFlagged) {
    rung = "manager_flagged";
    nextAction = "informed";
  } else if (chaseCount >= 2 || (daysSinceChase !== null && daysSinceChase >= 4)) {
    rung = "escalation_suggested";
    nextAction = "escalate";
  } else if (lastChaseAt !== null && Date.now() - lastChaseAt < _MS_24H) {
    rung = "chased_recently";
    nextAction = "wait";
  } else if (lastChaseAt !== null) {
    rung = "awaiting_response";
    nextAction = "send_chase";
  } else {
    rung = "awaiting";
    nextAction = "send_chase";
  }

  return {
    rung,
    label: RUNG_LABEL[rung],
    chase_count: chaseCount,
    last_chase_at: lastChaseAt ? new Date(lastChaseAt).toISOString() : null,
    days_since_chase: daysSinceChase,
    manager_flagged: managerFlagged,
    manager_flagged_at: managerFlaggedAt ? new Date(managerFlaggedAt).toISOString() : null,
    next_action: nextAction,
  };
}
